'use client';

import { usePathname } from 'next/navigation';
import Script from 'next/script';
import { type JSX, useEffect, useRef } from 'react';
import { initializeSnapchatPixel } from '@/lib/analytics/providers/snapchat';
import { useImpersonationStore } from '@/lib/impersonation/store';
import { logger } from '@/lib/telemetry/logger';

export function SnapchatPixel({
  environment,
}: {
  environment: string | undefined;
}): JSX.Element | null {
  const pathname = usePathname();
  const isImpersonating = useImpersonationStore((state) => state.isActive);
  const lastPath = useRef<string | null>(null);
  const enabled = environment === 'production' && !isImpersonating;

  useEffect(() => {
    if (!enabled || pathname === null || lastPath.current === pathname) return;
    try {
      initializeSnapchatPixel();
      window.snaptr?.('track', 'PAGE_VIEW');
      lastPath.current = pathname;
    } catch {
      logger.warn('Snapchat page view failed', { eventName: 'PAGE_VIEW' });
    }
  }, [enabled, pathname]);

  if (!enabled) return null;
  return (
    <Script
      id="snapchat-pixel"
      src="https://sc-static.net/scevent.min.js"
      strategy="afterInteractive"
      onError={() => logger.warn('Snapchat pixel script failed to load', { vendor: 'snapchat' })}
    />
  );
}
